import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { solutionsData } from '../data/solutions';
import Contact from '../components/Contact';

const SolutionDetail = () => {
  const { slug } = useParams();
  const solution = solutionsData[slug];

  // If page not found
  if (!solution) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex flex-col items-center justify-center">
        <h1 className="text-4xl font-space-grotesk text-[#FFD700] mb-4">Solution Not Found</h1>
        <Link to="/" className="text-white hover:text-[#FFD700] underline uppercase font-space-grotesk tracking-widest text-sm">
          Return to Home
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-[#050505] min-h-screen text-white font-inter">
      {/* Header / Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-48 md:pb-32 overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-[#050505]/40 to-[#050505] z-10"></div>
          <img 
            src={solution.image} 
            alt={solution.title}
            className="w-full h-full object-cover opacity-60 mix-blend-luminosity"
          />
        </div>
        
        <div className="container mx-auto px-6 relative z-20">
          <Link to="/" className="inline-flex items-center gap-4 text-xs font-space-grotesk uppercase tracking-widest text-gray-400 hover:text-[#FFD700] transition-colors mb-16 group">
            <span className="w-8 h-8 rounded-full border border-gray-600 group-hover:border-[#FFD700] flex items-center justify-center transition-colors">
              <ArrowLeft size={14} className="transform group-hover:-translate-x-1 transition-transform" />
            </span>
            Back to Overview
          </Link>
          
          <div className="max-w-4xl">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="flex items-center gap-6 mb-8"
            >
              <span className="text-5xl md:text-7xl font-space-grotesk font-bold text-transparent bg-clip-text bg-gradient-to-b from-[#FFD700] to-yellow-800 opacity-30">
                {solution.id}
              </span>
              <div className="w-16 h-[1px] bg-[#FFD700]"></div>
            </motion.div>
            
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-5xl md:text-7xl font-space-grotesk font-bold uppercase tracking-tighter leading-[0.9] mb-8"
            >
              {solution.title}
            </motion.h1>
            
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-2xl md:text-3xl text-gray-300 font-space-grotesk font-light mb-8"
            >
              {solution.subtitle}
            </motion.h2>
            
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-xl text-gray-400 font-light leading-relaxed max-w-2xl"
            >
              {solution.description}
            </motion.p>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-24 relative z-10">
        <div className="container mx-auto px-6">
          {slug === 'mobile-app' && (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="mb-24 w-full flex flex-col items-center"
            >
              <div className="w-full h-auto rounded-2xl overflow-hidden border border-white/10 shadow-[0_0_50px_rgba(255,215,0,0.15)] bg-black mb-12">
                <video 
                  src="https://customer-assets-39nsmqrw.emergentagent.net/job_imvula-refresh/artifacts/ehovlmfg_iMvula%20App%20Promo%202026.mp4" 
                  controls 
                  autoPlay 
                  loop 
                  muted 
                  playsInline
                  className="w-full h-auto"
                />
              </div>
              <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
                <a href="https://play.google.com/store/apps/details?id=com.oraxsdi.imvula&hl=en_ZA" target="_blank" rel="noopener noreferrer" className="hover:scale-105 transition-transform">
                  <img src="https://customer-assets-39nsmqrw.emergentagent.net/job_imvula-refresh/artifacts/4vm1r821_StoreAsset%203Play%20White.webp" alt="Get it on Google Play" className="h-16 w-auto" />
                </a>
                <a href="https://apps.apple.com/za/app/imvula-quality-protection/id1430649399" target="_blank" rel="noopener noreferrer" className="hover:scale-105 transition-transform">
                  <img src="https://customer-assets-39nsmqrw.emergentagent.net/job_imvula-refresh/artifacts/nbteu2s7_StoreAsset%206App%20White.webp" alt="Download on the App Store" className="h-16 w-auto" />
                </a>
              </div>
            </motion.div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-16 relative">
            {solution.features.map((feature, idx) => (
              <motion.div 
                key={`feature-${idx}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-10%" }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                className="group relative flex flex-col justify-start"
              >
                <div className="absolute -left-6 top-0 w-[2px] h-0 bg-[#FFD700] group-hover:h-full transition-all duration-500"></div>
                <h3 className="text-2xl font-space-grotesk font-bold uppercase tracking-tight mb-4 text-white group-hover:text-[#FFD700] transition-colors">
                  {feature.title}
                </h3>
                <p className="text-gray-400 font-light leading-relaxed text-lg flex-grow-0">
                  {feature.text}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Shared Contact Section */}
      <div className="border-t border-white/5">
        <Contact />
      </div>
    </div>
  );
};

export default SolutionDetail;
